import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileEdit,
  Eye,
  ClipboardCheck,
  CheckCircle2,
  Send,
  XCircle,
  AlertTriangle,
  Inbox
} from 'lucide-react';

const STATUS_CONFIG = {
  draft: {
    label: 'In Progress',
    color: 'bg-gray-100 text-gray-700',
    icon: FileEdit,
    hint: 'Finish and submit',
  },
  ready_for_review: {
    label: 'Awaiting Review',
    color: 'bg-yellow-100 text-yellow-700',
    icon: Eye,
    hint: 'Waiting for admin review',
  },
  under_review: {
    label: 'Under Review',
    color: 'bg-blue-100 text-blue-700',
    icon: ClipboardCheck,
    hint: 'Admin is reviewing',
  },
  approved: {
    label: 'Ready to Submit',
    color: 'bg-green-100 text-green-700',
    icon: CheckCircle2,
    hint: 'Submit to Upwork',
  },
  submitted: {
    label: 'Sent to Client',
    color: 'bg-purple-100 text-purple-700',
    icon: Send,
    hint: 'Waiting for client response',
  },
  rejected: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-700',
    icon: XCircle,
    hint: 'Review feedback and revise',
  },
  submission_failed: {
    label: 'Failed',
    color: 'bg-orange-100 text-orange-700',
    icon: AlertTriangle,
    hint: 'Retry submission',
  },
};

// Statuses that only apply when review workflow is active
const REVIEW_ONLY_STATUSES = new Set(['ready_for_review', 'under_review']);

const DashboardPipelineCards = ({ counts, loading, workflowMode = 'hybrid' }) => {
  const navigate = useNavigate();
  const hideReviewStatuses = workflowMode === 'auto_approve';

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-lg p-4 shadow-sm animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
            <div className="h-6 bg-gray-200 rounded w-10"></div>
          </div>
        ))}
      </div>
    );
  }

  const visibleStatuses = Object.entries(STATUS_CONFIG)
    .filter(([status]) => {
      if (hideReviewStatuses && REVIEW_ONLY_STATUSES.has(status)) return false;
      return counts[status] > 0;
    });

  if (visibleStatuses.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
          <Inbox className="h-6 w-6 text-gray-400" />
        </div>
        <h3 className="text-sm font-medium text-gray-900 mb-1">No proposals yet</h3>
        <p className="text-sm text-gray-500 mb-3">Start by finding a job and creating a proposal.</p>
        <button
          onClick={() => navigate('/jobs')}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
        >
          Find Work
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {visibleStatuses.map(([status, config]) => {
        const Icon = config.icon;
        return (
          <button
            key={status}
            onClick={() => navigate(`/proposals?status=${status}`)}
            className={`${config.color} rounded-lg p-4 text-left hover:opacity-80 transition-opacity`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Icon className="h-4 w-4" />
              <span className="text-xs font-medium">{config.label}</span>
            </div>
            <p className="text-2xl font-bold">{counts[status]}</p>
            {config.hint && (
              <p className="text-[10px] mt-1 opacity-70">{config.hint}</p>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default DashboardPipelineCards;
